// Cómo se llaman en pantalla las cifras que este módulo sabe medir.
//
// Aparte del resto de los textos por un motivo que no es de tamaño: estas claves
// son un CONTRATO con el catálogo de analítica (`backend/analytics.js`), no copia
// de una pantalla. Cada `labelKey` que aquel fichero declara tiene aquí su
// traducción en los cuatro idiomas, y una que falte no rompe nada — sale el id
// hecho legible, en inglés, en medio de una pantalla en castellano.
//
// La ayuda de un dataset (`help`) es lo que alguien lee en el desplegable de
// Explorar antes de elegirlo. Dice qué es UNA FILA, que es la pregunta que de
// verdad distingue dos conjuntos de datos parecidos.

const analytics = {
  es: {
    plans: {
      label: 'Planes de mantenimiento',
      help: 'Una fila por plan preventivo. No tiene historia: para lo que se hizo, usa los partes',
    },
    records: {
      label: 'Mantenimientos hechos',
      help: 'Una fila por trabajo de mantenimiento completado. No lleva coste',
    },
    dates: {
      due: 'Próximo vencimiento',
      lastDone: 'Última vez hecho',
      starts: 'Inicio del plan',
      completed: 'Fecha de realización',
    },
    metrics: {
      plans: 'Planes',
      active: 'Activos',
      overdue: 'Vencidos',
      daysOverdue: 'Días de retraso',
      compliance: 'Cumplimiento',
      doneCount: 'Veces realizado',
      records: 'Mantenimientos hechos',
      steps: 'Puntos de la lista',
      stepsDone: 'Puntos marcados',
      checklist: 'Listas completadas',
    },
    dimensions: {
      subject: 'Sobre qué',
      subjectType: 'Tipo de sujeto',
      kind: 'Tipo de trabajo',
      status: 'Estado',
      mode: 'Por calendario o por uso',
      assignee: 'Responsable',
      recordSubject: 'Sobre qué',
      recordSubjectType: 'Tipo de sujeto',
      plan: 'Plan',
      completedBy: 'Quién lo hizo',
    },
  },
  ca: {
    plans: {
      label: 'Plans de manteniment',
      help: 'Una fila per pla preventiu. No té història: per al que es va fer, fes servir els parts',
    },
    records: {
      label: 'Manteniments fets',
      help: 'Una fila per treball de manteniment completat. No porta cost',
    },
    dates: {
      due: 'Pròxim venciment',
      lastDone: 'Última vegada fet',
      starts: 'Inici del pla',
      completed: 'Data de realització',
    },
    metrics: {
      plans: 'Plans',
      active: 'Actius',
      overdue: 'Vençuts',
      daysOverdue: 'Dies de retard',
      compliance: 'Compliment',
      doneCount: 'Vegades realitzat',
      records: 'Manteniments fets',
      steps: 'Punts de la llista',
      stepsDone: 'Punts marcats',
      checklist: 'Llistes completades',
    },
    dimensions: {
      subject: 'Sobre què',
      subjectType: 'Tipus de subjecte',
      kind: 'Tipus de treball',
      status: 'Estat',
      mode: 'Per calendari o per ús',
      assignee: 'Responsable',
      recordSubject: 'Sobre què',
      recordSubjectType: 'Tipus de subjecte',
      plan: 'Pla',
      completedBy: 'Qui ho va fer',
    },
  },
  en: {
    plans: {
      label: 'Maintenance plans',
      help: 'One row per preventive plan. It has no history: for what was done, use the records',
    },
    records: {
      label: 'Maintenance done',
      help: 'One row per completed maintenance job. It carries no cost',
    },
    dates: {
      due: 'Next due',
      lastDone: 'Last done',
      starts: 'Plan starts',
      completed: 'Completed',
    },
    metrics: {
      plans: 'Plans',
      active: 'Active',
      overdue: 'Overdue',
      daysOverdue: 'Days overdue',
      compliance: 'Compliance',
      doneCount: 'Times done',
      records: 'Jobs done',
      steps: 'Checklist steps',
      stepsDone: 'Steps ticked',
      checklist: 'Checklist completion',
    },
    dimensions: {
      subject: 'Subject',
      subjectType: 'Subject kind',
      kind: 'Kind of work',
      status: 'Status',
      mode: 'By calendar or by use',
      assignee: 'Assignee',
      recordSubject: 'Subject',
      recordSubjectType: 'Subject kind',
      plan: 'Plan',
      completedBy: 'Done by',
    },
  },
  fr: {
    plans: {
      label: 'Plans de maintenance',
      help: 'Une ligne par plan préventif. Sans historique : pour ce qui a été fait, utilisez les interventions',
    },
    records: {
      label: 'Interventions réalisées',
      help: 'Une ligne par intervention terminée. Sans coût',
    },
    dates: {
      due: 'Prochaine échéance',
      lastDone: 'Dernière réalisation',
      starts: 'Début du plan',
      completed: 'Date de réalisation',
    },
    metrics: {
      plans: 'Plans',
      active: 'Actifs',
      overdue: 'En retard',
      daysOverdue: 'Jours de retard',
      compliance: 'Conformité',
      doneCount: 'Fois réalisé',
      records: 'Interventions',
      steps: 'Points de la liste',
      stepsDone: 'Points cochés',
      checklist: 'Listes complétées',
    },
    dimensions: {
      subject: 'Sur quoi',
      subjectType: 'Type de sujet',
      kind: 'Type de travail',
      status: 'Statut',
      mode: 'Par calendrier ou par usage',
      assignee: 'Responsable',
      recordSubject: 'Sur quoi',
      recordSubjectType: 'Type de sujet',
      plan: 'Plan',
      completedBy: 'Réalisé par',
    },
  },
}

/**
 * Adds the analytics vocabulary to the module's own catalogue, locale by locale.
 *
 * Merged here rather than pasted into the big literal so the block stays
 * readable and so adding a metric is one line in two files instead of four
 * edits in the middle of eight hundred.
 */
export function withAnalytics(base, namespace) {
  const merged = {}
  for (const [locale, catalogue] of Object.entries(base)) {
    merged[locale] = {
      ...catalogue,
      [namespace]: { ...catalogue[namespace], analytics: analytics[locale] },
    }
  }
  return merged
}

export default analytics
